import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { MAX_STAR_ENTRY, MAX_STAR_GOAL, STAR_GOAL_NAME_MAX, starBalance } from '@kraftwerk/shared';
import { currentOwnerId } from '../../auth.js';
import { db } from '../../db/local.js';
import type { LocalHouseholdMember, LocalStarEntry } from '../../db/local.js';
import { addStarEntry, deleteStarEntry, setTaskStars, starEntries } from '../../db/stars.js';
import { syncOnce } from '../../sync/engine.js';
import { useToday } from '../../hooks/useToday.js';
import { formatDay } from '../../format.js';

/**
 * Sterne (26.10.2026): für Kinder, die mit Geld noch nichts anfangen können.
 * Erledigte Aufgaben bringen Sterne, die Eltern lösen sie für die Belohnung
 * ein („10 Sterne = Eis“). Der Stand ist die Summe der Zeilen.
 */
export function StarChart({
  child,
  mode,
}: {
  child: LocalHouseholdMember;
  mode: 'child' | 'parent';
}): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const me = currentOwnerId();
  const entries = useLiveQuery(
    () => starEntries(child.userId, db),
    [child.userId],
    [] as LocalStarEntry[],
  );
  const balance = starBalance(entries);
  const on = child.taskStars === true;
  // Das Kind sieht Sterne erst, wenn die Eltern sie eingeschaltet haben oder
  // noch welche da sind — eine leere Sternenkachel wäre nur Rauschen.
  if (mode === 'child' && !on && entries.length === 0) return null;
  const goal =
    child.starGoalName && child.starGoalStars
      ? { name: child.starGoalName, stars: child.starGoalStars }
      : null;
  const reached = goal !== null && balance >= goal.stars;
  const percent = goal ? Math.max(0, Math.min(100, (balance / goal.stars) * 100)) : 0;

  return (
    <section className="flex flex-col gap-2" aria-label={t('stars.heading')}>
      <p className="kw-label">{t('stars.heading')}</p>
      <p className="kw-figure text-4xl">
        <span aria-hidden="true">★ {balance}</span>
        <span className="sr-only">{t('stars.count', { count: balance })}</span>
      </p>
      {goal ? (
        <>
          <p className={`text-sm ${reached ? 'text-good' : ''}`}>
            {reached
              ? t('stars.reached', { name: goal.name })
              : t('stars.missing', { count: goal.stars - balance, name: goal.name })}
          </p>
          <span
            className="kw-bar kw-bar-thin"
            role="progressbar"
            aria-label={goal.name}
            aria-valuemin={0}
            aria-valuemax={goal.stars}
            aria-valuenow={Math.max(0, Math.min(goal.stars, balance))}
          >
            <i
              style={{
                width: `${percent}%`,
                backgroundColor: reached ? 'var(--color-good)' : undefined,
              }}
            />
          </span>
        </>
      ) : mode === 'parent' && on ? (
        <p className="text-xs text-ink-muted">{t('stars.noGoal')}</p>
      ) : null}

      {mode === 'parent' ? <StarSettings child={child} /> : null}
      {mode === 'parent' && (on || balance > 0) ? (
        <StarActions child={child} balance={balance} goal={goal} />
      ) : null}

      {entries.length > 0 ? (
        <ul className="flex flex-col divide-y divide-line">
          {entries.slice(0, 10).map((entry) => (
            <li key={entry.id} className="flex min-h-11 items-center gap-3 py-1">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{entry.note || t(`stars.kind.${entry.kind}`)}</p>
                <p className="text-xs text-ink-muted">
                  {formatDay(entry.date, 'short', i18n.language)} · {t(`stars.kind.${entry.kind}`)}
                </p>
              </div>
              <span
                className={`font-semibold tabular-nums ${entry.kind === 'redeem' ? '' : 'text-good'}`}
              >
                {entry.kind === 'redeem' ? '−' : '+'}
                {entry.stars} ★
              </span>
              {entry.kind !== 'task' && me !== null && (entry.userId ?? me) === me ? (
                <button
                  type="button"
                  onClick={() => void deleteStarEntry(entry, db)}
                  aria-label={t('stars.removeNamed', {
                    what: entry.note || t(`stars.kind.${entry.kind}`),
                  })}
                  className="kw-btn kw-btn-quiet kw-btn-small px-3"
                >
                  ×
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/** Ein- und Ausschalten und die Belohnung — schreibt nur der Server. */
function StarSettings({ child }: { child: LocalHouseholdMember }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const ids = { name: useId(), stars: useId() };
  const on = child.taskStars === true;
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(child.starGoalName ?? '');
  const [stars, setStars] = useState(child.starGoalStars ? String(child.starGoalStars) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const count = Number(stars);
  const starsValid = Number.isInteger(count) && count >= 1 && count <= MAX_STAR_GOAL;
  // Belohnung ganz oder gar nicht: ein Name ohne Zahl wäre nie erreichbar.
  const goalValid =
    (name.trim() === '' && stars.trim() === '') || (name.trim() !== '' && starsValid);

  const save = (input: Parameters<typeof setTaskStars>[1], close: boolean) => {
    setBusy(true);
    setError(null);
    setTaskStars(child.userId, input)
      // Die Mitgliedschaft schreibt der Server — erst der Abgleich zeigt den neuen Stand.
      .then(() => syncOnce())
      .then(() => {
        if (close) setOpen(false);
      })
      .catch(() => setError(t('children.offline')))
      .finally(() => setBusy(false));
  };
  const goal = {
    goalName: child.starGoalName ?? null,
    goalStars: child.starGoalStars ?? null,
  };

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        aria-pressed={on}
        disabled={busy}
        onClick={() => save({ enabled: !on, ...goal }, false)}
        className="kw-toggle self-start"
      >
        {t('stars.toggle')}
      </button>
      <p className="text-xs text-ink-muted">
        {on && child.starsSince
          ? t('stars.on', {
              date: formatDay(child.starsSince.slice(0, 10), 'short', i18n.language),
            })
          : t('stars.off', { name: child.displayName })}
      </p>
      {on && !open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="kw-btn kw-btn-quiet kw-btn-small self-start px-3"
        >
          {child.starGoalName ? t('stars.changeGoal') : t('stars.setGoal')}
        </button>
      ) : null}
      {on && open ? (
        <form
          className="flex flex-col gap-2 rounded-xl bg-surface-raised p-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!goalValid) return;
            const cleared = name.trim() === '';
            save(
              {
                enabled: true,
                goalName: cleared ? null : name.trim(),
                goalStars: cleared ? null : count,
              },
              true,
            );
          }}
        >
          <p className="kw-label">{t('stars.goalHeading')}</p>
          <div className="flex gap-2">
            <label htmlFor={ids.name} className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="kw-label">{t('stars.goalName')}</span>
              <input
                id={ids.name}
                value={name}
                maxLength={STAR_GOAL_NAME_MAX}
                onChange={(event) => setName(event.target.value)}
                placeholder={t('stars.goalNamePlaceholder')}
                className="kw-field"
              />
            </label>
            <label htmlFor={ids.stars} className="flex w-24 flex-col gap-1">
              <span className="kw-label">{t('stars.goalStars')}</span>
              <input
                id={ids.stars}
                inputMode="numeric"
                value={stars}
                onChange={(event) => setStars(event.target.value)}
                placeholder="10"
                aria-invalid={stars !== '' && !starsValid}
                className="kw-field"
              />
            </label>
          </div>
          {goalValid ? (
            <p className="text-xs text-ink-muted">{t('stars.goalHint')}</p>
          ) : (
            <p role="alert" className="text-xs text-danger">
              {t('stars.goalInvalid', { max: MAX_STAR_GOAL })}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <button type="submit" disabled={busy || !goalValid} className="kw-btn kw-btn-small">
              {t('stars.saveGoal')}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="kw-btn kw-btn-quiet kw-btn-small"
            >
              {t('pocket.cancel')}
            </button>
          </div>
        </form>
      ) : null}
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Extra-Sterne geben oder für die Belohnung einlösen. */
function StarActions({
  child,
  balance,
  goal,
}: {
  child: LocalHouseholdMember;
  balance: number;
  goal: { name: string; stars: number } | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const today = useToday();
  const ids = { stars: useId(), note: useId() };
  const [action, setAction] = useState<'bonus' | 'redeem' | null>(null);
  const [stars, setStars] = useState('1');
  const [note, setNote] = useState('');
  const count = Number(stars);
  // Mehr einlösen als da ist ergäbe einen Stand, den es nicht gibt.
  const limit = action === 'redeem' ? Math.min(balance, MAX_STAR_ENTRY) : MAX_STAR_ENTRY;
  const valid = Number.isInteger(count) && count >= 1 && count <= limit;

  const book = (kind: 'bonus' | 'redeem', amount: number, text: string | null) => {
    if (!today) return;
    void addStarEntry(
      {
        childId: child.userId,
        householdId: child.householdId,
        kind,
        stars: amount,
        date: today,
        note: text,
      },
      db,
    ).then(() => {
      setAction(null);
      setStars('1');
      setNote('');
    });
  };

  if (action === null) {
    return (
      <div className="flex flex-wrap gap-2">
        {goal && balance >= goal.stars ? (
          <button
            type="button"
            onClick={() => book('redeem', goal.stars, goal.name)}
            className="kw-btn kw-btn-small"
          >
            {t('stars.redeemGoal', { name: goal.name, count: goal.stars })}
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => setAction('bonus')}
          className="kw-btn kw-btn-quiet kw-btn-small px-3"
        >
          {t('stars.bonus')}
        </button>
        {balance > 0 ? (
          <button
            type="button"
            onClick={() => setAction('redeem')}
            className="kw-btn kw-btn-quiet kw-btn-small px-3"
          >
            {t('stars.redeem')}
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <form
      className="flex flex-col gap-2 rounded-xl bg-surface-raised p-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid) book(action, count, note.trim() || null);
      }}
    >
      <p className="kw-label">{t(`stars.${action}`)}</p>
      <div className="flex gap-2">
        <label htmlFor={ids.stars} className="flex w-24 flex-col gap-1">
          <span className="kw-label">{t('stars.amount')}</span>
          <input
            id={ids.stars}
            inputMode="numeric"
            value={stars}
            onChange={(event) => setStars(event.target.value)}
            aria-invalid={!valid}
            className="kw-field"
          />
        </label>
        <label htmlFor={ids.note} className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="kw-label">{t('stars.note')}</span>
          <input
            id={ids.note}
            value={note}
            maxLength={200}
            onChange={(event) => setNote(event.target.value)}
            className="kw-field"
          />
        </label>
      </div>
      {valid ? null : (
        <p role="alert" className="text-xs text-danger">
          {t('stars.amountInvalid', { max: limit })}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <button type="submit" disabled={!valid} className="kw-btn kw-btn-small">
          {t('stars.book')}
        </button>
        <button
          type="button"
          onClick={() => setAction(null)}
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {t('pocket.cancel')}
        </button>
      </div>
    </form>
  );
}
